import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Shield, BarChart3 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";

const STORAGE_KEY = "em-cookie-consent-v1";

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveConsent(prefs) {
  const data = { ...prefs, ts: new Date().toISOString(), version: 1 };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {/* silent */}
  // Notify other listeners (e.g. footer "manage cookies" link)
  window.dispatchEvent(new CustomEvent("em-cookie-consent-changed", { detail: data }));
}

export function PreferencesModal({ children, onSaved }) {
  const initial = loadConsent() || { analytics: false };
  const [analytics, setAnalytics] = useState(initial.analytics || false);
  const [open, setOpen] = useState(false);

  const persist = (vals) => {
    saveConsent(vals);
    setOpen(false);
    onSaved?.(vals);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading font-light text-2xl text-ink tracking-tight">
            Préférences <em className="font-display text-accent">cookies</em>
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Nous utilisons des cookies pour assurer le fonctionnement de la plateforme. Choisissez ce que vous souhaitez activer.
        </p>

        <div className="space-y-3 mt-4">
          {/* Nécessaires — toujours activés */}
          <div className="bg-cream-50 border border-ink/10 rounded-md p-4 flex items-start gap-3">
            <Shield className="h-4 w-4 text-trust flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="font-heading font-medium text-sm text-ink">Strictement nécessaires</p>
                <span className="text-[10px] uppercase tracking-wider font-bold text-trust">Toujours actif</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Authentification (session, cookies HttpOnly), panier, sécurité, préférences d&apos;affichage. Sans eux, la plateforme ne fonctionne pas.
              </p>
            </div>
          </div>

          {/* Analytique */}
          <div className="bg-white border border-ink/10 rounded-md p-4 flex items-start gap-3">
            <BarChart3 className="h-4 w-4 text-ink/70 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="font-heading font-medium text-sm text-ink">Mesure d&apos;audience</p>
                <Switch
                  checked={analytics}
                  onCheckedChange={setAnalytics}
                  data-testid="cookie-toggle-analytics"
                />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Statistiques anonymes (Umami, sans cookies tiers) pour comprendre l&apos;usage de la plateforme et l&apos;améliorer. Aucun profilage, aucune publicité.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 mt-6 pt-4 border-t border-ink/10">
          <Button
            variant="outline"
            onClick={() => persist({ analytics: false })}
            data-testid="cookie-refuse-all"
            className="rounded-none"
          >
            Tout refuser
          </Button>
          <Button
            onClick={() => persist({ analytics })}
            data-testid="cookie-save-preferences"
            className="bg-ink hover:bg-ink/85 text-cream rounded-none"
          >
            Enregistrer
          </Button>
        </div>

        <p className="text-[10px] text-muted-foreground text-center mt-2">
          Conformément à la recommandation CNIL. <Link to="/legal/confidentialite" className="text-accent hover:underline" onClick={() => setOpen(false)}>Voir notre politique de confidentialité</Link>.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = loadConsent();
    if (!consent) setVisible(true);
    const onChange = () => setVisible(false);
    window.addEventListener("em-cookie-consent-changed", onChange);
    return () => window.removeEventListener("em-cookie-consent-changed", onChange);
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50 bg-white border border-ink/15 rounded-md shadow-luxe-md p-5"
    >
      <button
        onClick={() => setVisible(false)}
        data-testid="cookie-dismiss"
        aria-label="Fermer"
        className="absolute top-3 right-3 p-1 text-ink/40 hover:text-ink"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-3 mb-3">
        <div className="bg-cream-100 p-2 rounded-md flex-shrink-0">
          <Cookie className="h-4 w-4 text-accent" />
        </div>
        <div>
          <p className="text-label text-muted-foreground mb-1">Cookies</p>
          <h3 className="font-heading font-medium text-base text-ink leading-tight">
            Nous respectons votre vie privée.
          </h3>
        </div>
      </div>
      <p className="text-xs text-ink/70 leading-relaxed mb-4">
        Nous utilisons uniquement des cookies essentiels au fonctionnement de la plateforme. Vous pouvez en savoir plus ou choisir vos préférences.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          onClick={acceptAll}
          data-testid="cookie-accept"
          className="flex-1 bg-ink hover:bg-ink/85 text-cream rounded-none text-xs font-bold uppercase tracking-wider"
        >
          Tout accepter
        </Button>
        <PreferencesModal>
          <Button
            variant="outline"
            data-testid="cookie-preferences"
            className="flex-1 rounded-none text-xs font-bold uppercase tracking-wider"
          >
            Préférences
          </Button>
        </PreferencesModal>
      </div>
    </div>
  );
}
